import { isHovAmplifierEntryId } from "@/features/hoverIdle/hov/hovAmplifierModel";
import type {
  AdminProgressBuilderAmplifierLevelLog10Seed,
  AdminProgressBuilderClearedWallSeed,
  AdminProgressBuilderSeed,
} from "./adminProgressBuilderTypes";

export interface AdminProgressBuilderSeedStorage {
  load(): AdminProgressBuilderSeed | null;
  save(seed: AdminProgressBuilderSeed): void;
}

interface LocalStorageAdminProgressBuilderSeedStorageOptions {
  readonly storage?: Storage | null;
}

// v2 adds per-Amp log10 enhancement levels. Older v1 seeds are dropped on load
// so the panel re-syncs Amp inputs from the live bundle instead of silently
// rebuilding every Amp at level 0.
const ADMIN_PROGRESS_BUILDER_SEED_VERSION = 2;
// The Progress Builder seed is an Admin development setting ("which verification
// start point to build next"), not player progression or account data. It is
// keyed machine-locally without an ownerId so it survives progress reset and
// account deletion even when those change the resolved owner on reload.
const ADMIN_PROGRESS_BUILDER_SEED_KEY =
  "hover-idle:admin-progress-builder-seed";

export function createLocalStorageAdminProgressBuilderSeedStorage(
  options: LocalStorageAdminProgressBuilderSeedStorageOptions = {},
): AdminProgressBuilderSeedStorage {
  const storage = options.storage ?? resolveLocalStorage();

  return {
    load(): AdminProgressBuilderSeed | null {
      if (storage === null) {
        return null;
      }
      try {
        const raw = storage.getItem(ADMIN_PROGRESS_BUILDER_SEED_KEY);
        if (raw === null) {
          return null;
        }
        return decodeStoredSeed(JSON.parse(raw) as unknown);
      } catch {
        return null;
      }
    },

    save(seed: AdminProgressBuilderSeed): void {
      if (storage === null) {
        return;
      }
      try {
        storage.setItem(
          ADMIN_PROGRESS_BUILDER_SEED_KEY,
          JSON.stringify({
            version: ADMIN_PROGRESS_BUILDER_SEED_VERSION,
            seed: normalizeSeed(seed),
          }),
        );
      } catch {
        // Builder seed is a development aid; storage failure must not block the
        // admin panel or gameplay.
      }
    },
  };
}

function decodeStoredSeed(value: unknown): AdminProgressBuilderSeed | null {
  if (!isRecord(value)) {
    return null;
  }
  if (value.version !== ADMIN_PROGRESS_BUILDER_SEED_VERSION) {
    return null;
  }
  if (!isRecord(value.seed)) {
    return null;
  }
  return normalizeSeed(value.seed);
}

function normalizeSeed(value: unknown): AdminProgressBuilderSeed {
  const record: Readonly<Record<string, unknown>> = isRecord(value)
    ? value
    : {};
  return {
    hovLog10: toFiniteNumber(record.hovLog10),
    hovCoreLog10: toFiniteNumber(record.hovCoreLog10),
    oHovPower: toFiniteNumber(record.oHovPower),
    currentOHovElapsedMs: toFiniteNumber(record.currentOHovElapsedMs),
    mHovCount: toFiniteNumber(record.mHovCount),
    nHovCount: toFiniteNumber(record.nHovCount),
    amplifierLevelLog10ByEntryId: toAmplifierLevelLog10(
      record.amplifierLevelLog10ByEntryId,
    ),
    acquiredHovSkillIds: toStringArray(record.acquiredHovSkillIds),
    unlockedAchievementIds: toStringArray(record.unlockedAchievementIds),
    activeWallId:
      typeof record.activeWallId === "string" ? record.activeWallId : null,
    activeWallElapsedMs: toFiniteNumber(record.activeWallElapsedMs),
    clearedWalls: toClearedWalls(record.clearedWalls),
  };
}

function toFiniteNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function toAmplifierLevelLog10(
  value: unknown,
): AdminProgressBuilderAmplifierLevelLog10Seed {
  if (!isRecord(value)) {
    return {};
  }
  const result: AdminProgressBuilderAmplifierLevelLog10Seed = {};
  for (const [key, raw] of Object.entries(value)) {
    if (!isHovAmplifierEntryId(key)) {
      continue;
    }
    if (typeof raw === "number" && Number.isFinite(raw)) {
      result[key] = raw;
    }
  }
  return result;
}

function toStringArray(value: unknown): readonly string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === "string");
}

function toClearedWalls(
  value: unknown,
): readonly AdminProgressBuilderClearedWallSeed[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const result: AdminProgressBuilderClearedWallSeed[] = [];
  for (const entry of value) {
    if (
      isRecord(entry) &&
      typeof entry.id === "string" &&
      typeof entry.bestClearMs === "number" &&
      Number.isFinite(entry.bestClearMs)
    ) {
      result.push({ id: entry.id, bestClearMs: entry.bestClearMs });
    }
  }
  return result;
}

function resolveLocalStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
